/**
 * Mockup pictures for a placement that isn't on the website map (a
 * newsletter, the post + story + reel): thumbnails you can reorder, crop
 * and remove, plus upload / paste from clipboard. Up to 8 pictures. Used on
 * Settings → Placements & prices (standard pictures) and on a partnership
 * page's Placements tab (the campaign's own).
 */
import { useToast } from '../ui/Toast';
import { pastedPicture, readPicture } from '../../lib/api/placementMap';
import { useCropper } from './useCropper';

const STYLES = `
.mg { display: grid; gap: 8px; font-size: 12px; }
.mg-gal { display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 6px; }
.mg-gal .g { position: relative; border-radius: 6px; overflow: hidden; border: 1px solid var(--border); background: #fff; }
.mg-gal img { width: 100%; height: 110px; object-fit: cover; display: block; }
.mg-gal .ga { position: absolute; left: 0; right: 0; bottom: 0; display: flex; justify-content: space-between; background: rgba(255,255,255,0.9); }
.mg-gal .ga button { border: 0; background: transparent; cursor: pointer; font-size: 11px; padding: 2px 6px; color: var(--ink-3); }
.mg-gal .ga button:disabled { opacity: 0.3; }
.mg-add { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
`;

export default function MockupGallery({
  images,
  onChange,
}: {
  images: string[];
  /** Saves the new list; the gallery shows the saved result. */
  onChange: (next: string[]) => Promise<unknown> | void;
}) {
  const toast = useToast();
  const { crop, element: cropper } = useCropper();

  const cropImage = async (i: number) => {
    const img = new Image();
    img.src = images[i];
    await img.decode().catch(() => undefined);
    const pic = await crop({ image: images[i], width: img.naturalWidth || 1200, height: img.naturalHeight || 800 });
    if (pic && pic.image !== images[i]) await onChange(images.map((x, k) => (k === i ? pic.image : x)));
  };
  const addImages = async (files: FileList | File[] | null) => {
    if (!files?.length) return;
    try {
      const pics: string[] = [];
      for (const f of Array.from(files)) {
        const pic = await crop(await readPicture(f));
        if (pic) pics.push(pic.image);
      }
      if (!pics.length) return;
      if (images.length + pics.length > 8) toast.push('Up to 8 pictures — the rest were left out.');
      await onChange([...images, ...pics].slice(0, 8));
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Could not read the picture.');
    }
  };
  const moveImage = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="mg">
      <style>{STYLES}</style>
      {cropper}
      {images.length > 0 && (
        <div className="mg-gal">
          {images.map((src, i) => (
            <div key={i} className="g">
              <img src={src} alt="" />
              <div className="ga">
                <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} aria-label="Move left">←</button>
                <button type="button" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} aria-label="Move right">→</button>
                <button type="button" onClick={() => cropImage(i)} aria-label="Crop picture" title="Crop">✂</button>
                <button type="button" onClick={() => onChange(images.filter((_, k) => k !== i))} aria-label="Remove picture">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mg-add">
        <label className="link" style={{ cursor: 'pointer', fontSize: 12 }}>
          {images.length ? 'Add more pictures…' : 'Upload pictures…'}
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => { const input = e.currentTarget; addImages(input.files).finally(() => { input.value = ''; }); }}
          />
        </label>
        <button
          type="button"
          className="link"
          style={{ fontSize: 12 }}
          onClick={async () => {
            try { await addImages([await pastedPicture()]); } catch (err) { toast.push(err instanceof Error ? err.message : 'Could not paste.'); }
          }}
        >
          or paste from clipboard
        </button>
      </div>
    </div>
  );
}
